import { createApi } from '@reduxjs/toolkit/query/react';
import {
  BoardDTO,
  BoardDetailDTO,
  BoardSettlementResultDTO,
  BoardType,
  CreateBoardPayload,
  CreateBoardSettlementPayload,
  UpdateBoardPayload,
} from '../../models/board';
import { Response } from '../../models/shared';
import baseQueryWithReauth from './base';
import { eventsApi } from './events';
import { wireApi } from './wire';

export const boardsApi = createApi({
  reducerPath: 'boardsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['boards', 'board-detail'],
  endpoints: (build) => ({
    getBoards: build.query<BoardDTO[], string>({
      providesTags: (result, error, businessId) => [{ type: 'boards', id: businessId }],
      query: (businessId) => ({
        url: `/businesses/${businessId}/boards`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BoardDTO[]>) => response.data ?? [],
    }),
    getBoardById: build.query<BoardDTO | null, { businessId: string; boardId: string }>({
      providesTags: (result, error, { boardId }) => [{ type: 'board-detail', id: boardId }],
      query: ({ businessId, boardId }) => ({
        url: `/businesses/${businessId}/boards/${boardId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BoardDTO>) => response.data ?? null,
    }),
    getBoardDetail: build.query<BoardDetailDTO | null, { businessId: string; boardId: string }>({
      providesTags: (result, error, { boardId }) => [{ type: 'board-detail', id: boardId }],
      query: ({ businessId, boardId }) => ({
        url: `/businesses/${businessId}/boards/${boardId}/detail`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BoardDetailDTO>) => response.data ?? null,
    }),
    createBoard: build.mutation<BoardDTO, { businessId: string; payload: CreateBoardPayload }>({
      invalidatesTags: (result, error, { businessId }) => [{ type: 'boards', id: businessId }],
      query: ({ businessId, payload }) => ({
        url: `/businesses/${businessId}/boards`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: Response<BoardDTO>) => response.data!,
    }),
    updateBoard: build.mutation<BoardDTO | null, { businessId: string; boardId: string; payload: UpdateBoardPayload }>({
      invalidatesTags: (result, error, { businessId, boardId }) => [
        { type: 'boards', id: businessId },
        { type: 'board-detail', id: boardId },
      ],
      query: ({ businessId, boardId, payload }) => ({
        url: `/businesses/${businessId}/boards/${boardId}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: Response<BoardDTO>) => response.data ?? null,
    }),
    duplicateBoard: build.mutation<BoardDTO | null, { businessId: string; boardId: string }>({
      invalidatesTags: (result, error, { businessId }) => [{ type: 'boards', id: businessId }],
      query: ({ businessId, boardId }) => ({
        url: `/businesses/${businessId}/boards/${boardId}/duplicate`,
        method: 'POST',
      }),
      transformResponse: (response: Response<BoardDTO>) => response.data ?? null,
    }),
    createBoardSettlement: build.mutation<
    BoardSettlementResultDTO,
    { businessId: string; boardId: string; payload: CreateBoardSettlementPayload }
    >({
      invalidatesTags: (result, error, { businessId, boardId }) => [
        { type: 'boards', id: businessId },
        { type: 'board-detail', id: boardId },
      ],
      query: ({ businessId, boardId, payload }) => ({
        url: `/businesses/${businessId}/boards/${boardId}/settlements`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: Response<BoardSettlementResultDTO>) => response.data!,
      // Las transferencias creadas cambian el balance de cada evento, que se
      // lee desde otras APIs (liquidaciones del evento, listado de eventos).
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(wireApi.util.invalidateTags(['wire']));
          dispatch(eventsApi.util.invalidateTags(['events']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    deleteBoard: build.mutation<boolean, { businessId: string; boardId: string }>({
      invalidatesTags: (result, error, { businessId, boardId }) => [
        { type: 'boards', id: businessId },
        { type: 'board-detail', id: boardId },
      ],
      query: ({ businessId, boardId }) => ({
        url: `/businesses/${businessId}/boards/${boardId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Response<boolean>) => Boolean(response.data),
    }),
  }),
});

export const {
  useGetBoardsQuery,
  useGetBoardByIdQuery,
  useGetBoardDetailQuery,
  useCreateBoardMutation,
  useUpdateBoardMutation,
  useDuplicateBoardMutation,
  useCreateBoardSettlementMutation,
  useDeleteBoardMutation,
} = boardsApi;

export { BoardType };
